// =============================
// File: src/App.jsx (UPDATED)
// =============================
import React, { useState, useEffect, useLayoutEffect } from "react";
import { HashRouter as Router, Route, Routes, Navigate, useLocation } from "react-router-dom";
import axios from "axios";

import MainUser from "./user/MainUser";
import MainDriver from "./driver/MainDriver";
import MainAdmin from "./admin/MainAdmin";
import MainProvider from "./provider/MainProvider";
import MainFinalClient from "./finalClient/MainFinalClient";
import ConnectionHandler from "./ConnectionHandler";
import useTokenRefresh from "./useTokenRefresh";
import ProductChat from "./finalClient/chatbot/ProductChat";
import DeliveryAlertsCenter from "./delivery-alerts/DeliveryAlertsCenter";
import usePushNotifications from "./push/usePushNotifications";

import FastMaxLanding from "./web/FastMaxLanding";
import Login from "./web/Login";
import Register from "./web/Register";
import TermsAndConditions from "./web/TermsAndConditions";
import PrivacyPolicy from "./web/PrivacyPolicy";
import DeveloperDocs from "./web/DeveloperDocs";
import UserDocsViewer from "./UserDocsViewer";
import SoyTiendaPage from "./web/SoyTiendaPage";
import ProductPage from "./web/product/ProductPage";
import StorePage from "./web/store/StorePage";
import BuscarEntrega from "./web/BuscarEntrega";

const API = process.env.REACT_APP_API_URL || "";

function ScrollToTop() {
  const { pathname, hash, search } = useLocation();

  useLayoutEffect(() => {
    const scrollTarget = new URLSearchParams(search).get("scroll");
    if (scrollTarget) {
      queueMicrotask(() => {
        const el = document.getElementById(scrollTarget);
        if (el) {
          el.scrollIntoView({ behavior: "auto", block: "start" });
        } else {
          window.scrollTo({ top: 0, left: 0, behavior: "auto" });
        }
      });
      return;
    }

    if (hash) {
      const id = hash.replace(/^#/, "");
      queueMicrotask(() => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "auto", block: "start" });
        } else {
          window.scrollTo({ top: 0, left: 0, behavior: "auto" });
        }
      });
      return;
    }
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname, hash, search]);

  return null;
}

export default function App() {
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
  const [userId, setUserId] = useState(null);
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [role, setRole] = useState("");
  const [addresses, setAddresses] = useState([]);
  const [providerId, setProviderId] = useState(null);

  const handleLoginSuccess = (user_id, username, email, phone_number, role, addresses = [], provider_id) => {
    setIsUserLoggedIn(true);
    setUserId(user_id);
    setUsername(username);
    setEmail(email);
    setPhoneNumber(phone_number);
    setRole(role);
    setAddresses(addresses || []);
    setProviderId(provider_id);
  };

  const handleUpdateUser = async () => {
    if (!userId) return;
    const response = await axios.get(`${API}/api/user/${userId}`);
    setUsername(response.data.username);
    setEmail(response.data.email);
    setPhoneNumber(response.data.phone_number);
    setRole(response.data.role);
    setProviderId(response.data.provider_id);
  };

  const handleLogout = () => {
    setIsUserLoggedIn(false);
    localStorage.clear();
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    const storedUserId = localStorage.getItem("user_id");
    const storedUsername = localStorage.getItem("username");
    const storedEmail = localStorage.getItem("email");
    const storedPhoneNumber = localStorage.getItem("phone_number");
    const storedRole = localStorage.getItem("role");
    const storeProviderId = localStorage.getItem("providerId");
    let storedAddresses = [];
    try {
      storedAddresses = JSON.parse(localStorage.getItem("addresses")) || [];
    } catch (_) {}

    if (token && storedUserId && storedUsername && storedEmail && storedPhoneNumber && storedRole) {
      handleLoginSuccess(
        storedUserId,
        storedUsername,
        storedEmail,
        storedPhoneNumber,
        storedRole,
        storedAddresses,
        storeProviderId
      );
    }
  }, []);

  useTokenRefresh(isUserLoggedIn);
  usePushNotifications({
    enabled: isUserLoggedIn && ["admin", "provider", "customer"].includes(role),
    role,
    userId,
    providerId,
  });

  const currentHash = window.location.hash;
  const isDocsRoute = currentHash.includes('/user-docs') ||
                      currentHash.includes('/driver-docs') ||
                      currentHash.includes('/provider-docs') ||
                      currentHash.includes('/developers');

  if (isUserLoggedIn && isDocsRoute) {
    return (
      <>
        <ConnectionHandler />
        <Router>
          <ScrollToTop />
          <Routes>
            <Route path="/user-docs"           element={<UserDocsViewer role="customer" />} />
            <Route path="/user-docs/:slug"     element={<UserDocsViewer role="customer" />} />
            <Route path="/driver-docs"         element={<UserDocsViewer role="driver" />} />
            <Route path="/driver-docs/:slug"   element={<UserDocsViewer role="driver" />} />
            <Route path="/provider-docs"       element={<UserDocsViewer role="provider" />} />
            <Route path="/provider-docs/:slug" element={<UserDocsViewer role="provider" />} />
            <Route path="/developers"          element={<DeveloperDocs />} />
            <Route path="*" element={<RedirectToMainApp />} />
          </Routes>
        </Router>
      </>
    );
  }

  return (
    <>
      <ConnectionHandler />
      {isUserLoggedIn ? (
        <div className="container-fluid p-0">
          {role === "customer" && (
            <MainUser onLogout={handleLogout} onUpdateUser={handleUpdateUser} user_id={userId} username={username} email={email} phone_number={phoneNumber} addresses={addresses} />
          )}
          {role === "driver" && (
            <MainDriver onLogout={handleLogout} onUpdateUser={handleUpdateUser} user_id={userId} username={username} email={email} phone_number={phoneNumber} />
          )}
          {role === "admin" && (
            <MainAdmin onLogout={handleLogout} onUpdateUser={handleUpdateUser} user_id={userId} username={username} email={email} phone_number={phoneNumber} />
          )}
          {role === "provider" && (
            <MainProvider onLogout={handleLogout} onUpdateUser={handleUpdateUser} user_id={userId} username={username} email={email} phone_number={phoneNumber} />
          )}
          <DeliveryAlertsCenter role={role} userId={userId} providerId={providerId} />
        </div>
      ) : (
        <Router>
          <ScrollToTop />
          <Routes>
            {/* ── Static routes ── */}
            <Route path="/"        element={<SoyTiendaPage />} />
            <Route path="/tienda"  element={<FastMaxLanding />} />
            <Route path="/login"   element={<Login onLoginSuccess={handleLoginSuccess} />} />
            <Route path="/register" element={<Register />} />
            <Route path="/terms"   element={<TermsAndConditions />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/developers" element={<DeveloperDocs />} />

            <Route path="/user-docs"           element={<UserDocsViewer role="customer" />} />
            <Route path="/user-docs/:slug"     element={<UserDocsViewer role="customer" />} />
            <Route path="/driver-docs"         element={<UserDocsViewer role="driver" />} />
            <Route path="/driver-docs/:slug"   element={<UserDocsViewer role="driver" />} />
            <Route path="/provider-docs"       element={<UserDocsViewer role="provider" />} />
            <Route path="/provider-docs/:slug" element={<UserDocsViewer role="provider" />} />

            <Route path="/chat/:sku/:sellerId" element={<ProductChat />} />

            <Route path="/buscar" element={<BuscarEntrega />} />

            {/* ── Delivery tracking — unambiguous prefixed route ───────────────
                /#/entrega/12345 → MainFinalClient, useParams().deliveryId = "12345"
                No conflict with store slugs possible.
            ── */}
            <Route path="/entrega/:deliveryId" element={<MainFinalClient />} />

            {/* ── Product detail — two segments ── */}
            <Route path="/:storeSlug/:productSlug" element={<ProductPage />} />

            {/* ── Store profile — single text segment ── */}
            <Route path="/:storeSlug" element={<StorePage />} />

            {/* ── Fallback ── */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      )}
    </>
  );
}

function RedirectToMainApp() {
  useEffect(() => {
    window.close();
    if (!window.closed) window.location.href = '/#/';
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-brand-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-slate-600 font-montserrat">Redirigiendo...</p>
      </div>
    </div>
  );
}
